import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { CreateMediaUploadResponseSchema } from '../media/files.schemas';
import { PropertyStatusSchema, MarketingTypeSchema, PropertySummaryAddressSchema } from './properties.schemas';
import {
  BROWSER_END_LISTING_KINDS,
  BROWSER_PAGE_RUN_KINDS,
  BROWSER_RUN_KINDS,
  BrowserRunViewSchema,
  IMMOSCOUT24_LISTING_TYPES,
  PORTAL_LISTING_OPERATIONS,
  PORTAL_LISTING_STATUSES,
  PORTAL_READINESS_ITEMS,
  PublicationOptionSchema,
} from './portal.schemas';

// IMM-1381 (ADR 0064): the browser extension that writes ImmoScout24 in the agent's own ScoutManager tab.

const ScoutIdSchema = z
  .string()
  .trim()
  .regex(/^\d{1,20}$/);

export const BrowserListingsPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const BrowserIs24ListingSchema = z.object({
  listingType: z.enum(IMMOSCOUT24_LISTING_TYPES),
  portalListingId: z.string().nullable(),
  status: z.enum(PORTAL_LISTING_STATUSES).nullable(),
  live: z.boolean(),
  inFlight: z.enum(PORTAL_LISTING_OPERATIONS).nullable(),
  publicUrl: z.url().nullable(),
  lastTransmittedAt: z.iso.datetime().nullable(),
});
export type BrowserIs24Listing = z.infer<typeof BrowserIs24ListingSchema>;

export const BrowserListingItemSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  organizationId: refersTo('organization', z.uuid()),
  title: z.string(),
  objectNumber: z.string(),
  status: PropertyStatusSchema,
  marketingType: MarketingTypeSchema,
  askingPrice: z.number().nullable(),
  address: PropertySummaryAddressSchema,
  // Empty when the Objekt has never been on ImmoScout24; two rows for a Mehrfamilienhaus (residential + investment).
  is24: z.array(BrowserIs24ListingSchema),
});
export type BrowserListingItem = z.infer<typeof BrowserListingItemSchema>;

export const BrowserListingsResponseSchema = z.object({ data: z.array(BrowserListingItemSchema) });
export type BrowserListingsResponse = z.infer<typeof BrowserListingsResponseSchema>;

export const BrowserJobPathParamsSchema = z.object({ organizationId: z.uuid(), id: z.uuid() });

export const BrowserJobQuerySchema = z.object({
  // „Verknüpfen“: the job writes this IS24 listing instead of the Objekt's own (or a new one).
  attachPortalListingId: ScoutIdSchema.optional(),
});

export const BrowserTransmittedAttachmentSchema = z.object({
  mediaId: z.uuid(),
  contentHash: z.string().min(1),
  title: z.string(),
  floorplan: z.boolean(),
  titlePicture: z.boolean(),
  portalAttachmentId: z.string().regex(/^\d{1,20}$/),
});
export type BrowserTransmittedAttachment = z.infer<typeof BrowserTransmittedAttachmentSchema>;

export const BrowserJobMediaSchema = z.object({
  mediaId: z.uuid(),
  contentHash: z.string(),
  title: z.string(),
  floorplan: z.boolean(),
  titlePicture: z.boolean(),
  fileName: z.string(),
  downloadUrl: z.url(),
});
export type BrowserJobMedia = z.infer<typeof BrowserJobMediaSchema>;

// IMM-1407: job format 2 — every ScoutManager call built on the server; the extension only executes them.

/** The only API a job may call, on the environment's IS24 host. */
export const SCOUTMANAGER_API_PATH = '/scoutmanager/inserieren/api/';

const ScoutManagerPathSchema = z.string().startsWith(SCOUTMANAGER_API_PATH);

/**
 * One ScoutManager call. `path` is relative to the environment's IS24 origin; its `{name}` placeholders name `values`
 * (or `item` under `forEach`) and resolve to a Scout-ID or attachment id. `json`, `values` and `outcome` hold plain JSON
 * or one of four references, the only objects with a `$` key in a job:
 *
 * - `{ "$answer": "<call id>", "at"?: "<dotted path>", "as"?: "number" | "string" }`: an earlier call's parsed answer
 * - `{ "$value": "<name>" }`: a named value from `values`
 * - `{ "$merge": [<base>, <patch>] }`: patch laid over base, nested objects one level deep (a non-object base is `{}`)
 * - `{ "$newAttachment": { "before": "<call id>", "after": "<call id>" } }`: the one attachment id `after` listed that `before` did not
 */
export const ScoutManagerCallSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    step: z.enum(['FIELDS', 'PICTURES', 'CHECK']),
    method: z.enum(['GET', 'PUT', 'POST', 'DELETE']),
    path: ScoutManagerPathSchema,
    json: z.unknown().optional(),
    // The body is the job media's file, downloaded and sent by the extension.
    upload: z.object({ mediaId: z.uuid() }).strict().optional(),
    // Statuses outside 2xx that still count as done (a picture already gone answers 404).
    okStatuses: z.array(z.number().int()).optional(),
    // One request per attachment id an earlier call listed, minus `except`; `{item}` in the path.
    forEach: z
      .object({ attachmentIdsOf: z.string(), except: z.array(z.string()) })
      .strict()
      .optional(),
  })
  .strict();
export type ScoutManagerCall = z.infer<typeof ScoutManagerCallSchema>;

const ScoutManagerReadSchema = z.object({ path: ScoutManagerPathSchema, at: z.string().optional() }).strict();

export const BrowserPublishJobV2Schema = z
  .object({
    v: z.literal(2),
    listingType: z.enum(IMMOSCOUT24_LISTING_TYPES),
    portalListingId: z.string().nullable(),
    externalId: z.string(),
    fingerprint: z.string(),
    // The Objekt's fields as ScoutManager names them: laid over the listing by the save, and what the comparison and
    // the final check hold IS24's stored listing against.
    fields: z.record(z.string(), z.unknown()),
    // The comparison's reads of a listing already on IS24; null for a new one.
    read: z.object({ listing: ScoutManagerReadSchema, pictures: ScoutManagerReadSchema }).strict().nullable(),
    values: z.record(z.string(), z.unknown()),
    calls: z.array(ScoutManagerCallSchema),
    outcome: z
      .object({
        portalListingId: z.unknown(),
        // The call after which a new draft's Scout-ID is reported to immoOS; null when the listing exists.
        draftCreatedAfter: z.string().nullable(),
        attachments: z.array(BrowserTransmittedAttachmentSchema.extend({ portalAttachmentId: z.unknown() })),
        stored: z.unknown(),
      })
      .strict(),
    media: z.array(BrowserJobMediaSchema),
    attachments: z.array(BrowserTransmittedAttachmentSchema),
  })
  .strict();
export type BrowserPublishJobV2 = z.infer<typeof BrowserPublishJobV2Schema>;

// One format at a time (IMM-1407): the calls built here, which the extension only executes.
export const BrowserPublishJobSchema = BrowserPublishJobV2Schema;
export type BrowserPublishJob = z.infer<typeof BrowserPublishJobSchema>;

export const BrowserJobResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  readiness: z.object({
    ready: z.boolean(),
    // The ImmoScout24 blockers, the connection ones left out: the extension needs no IS24 connection.
    blockers: z.array(z.enum(PORTAL_READINESS_ITEMS)),
    contactName: z.string().nullable(),
    mediaCount: z.number().int(),
    hasCover: z.boolean(),
  }),
  // One per IS24 listing the Objekt resolves to; empty while it is not ready.
  jobs: z.array(BrowserPublishJobSchema),
});
export type BrowserJobResponse = z.infer<typeof BrowserJobResponseSchema>;

export const RecordBrowserOutcomeBodySchema = z
  .object({
    listingType: z.enum(IMMOSCOUT24_LISTING_TYPES),
    portalListingId: ScoutIdSchema,
    // `null` when IS24 did not keep every field: the listing is then not in step with the job it answers.
    fingerprint: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .nullable(),
    attachments: z.array(BrowserTransmittedAttachmentSchema).max(150),
  })
  .strict();
export type RecordBrowserOutcomeBody = z.infer<typeof RecordBrowserOutcomeBodySchema>;

// IMM-1408 „Verknüpfen“: keep the tab's Scout-ID on the Objekt without writing anything to ImmoScout24.
export const LinkBrowserListingBodySchema = z.object({ portalListingId: ScoutIdSchema }).strict();
export type LinkBrowserListingBody = z.infer<typeof LinkBrowserListingBodySchema>;

// IMM-1484: importing the agent's ImmoScout24 listings as Objekte. immoOS reads nothing from ImmoScout24 itself — the
// extension reads the listing in the agent's session and hands its ScoutManager data over.
export const BrowserImportStandortSchema = z.object({
  organizationId: refersTo('organization', z.uuid()),
  name: z.string(),
  members: z.array(z.object({ memberId: refersTo('member', z.uuid()), name: z.string() })),
});

export const BrowserImportOptionsResponseSchema = z.object({
  // Whether the member may both create Objekte and publish through the extension; without it every import is hidden.
  mayImport: z.boolean(),
  // The caller: zuständig for the import unless they pick another member.
  memberId: refersTo('member', z.uuid()),
  standorte: z.array(BrowserImportStandortSchema),
  defaultOrganizationId: refersTo('organization', z.uuid()).nullable(),
  // Every Scout-ID an Objekt of the workspace holds: those listings are already linked and never offered.
  linkedPortalListingIds: z.array(z.string()),
});
export type BrowserImportOptionsResponse = z.infer<typeof BrowserImportOptionsResponseSchema>;

/** The media label of a picture uploaded for an import (IMM-1484): only such a pending file is taken on import. */
export const BROWSER_IMPORT_PICTURE_DESCRIPTION = 'property-browser-import-picture';

export const BrowserImportPathParamsSchema = z.object({ organizationId: z.uuid() });

export const BrowserImportPictureUploadsBodySchema = z
  .object({
    pictures: z
      .array(
        z.object({
          fileName: z.string().trim().min(1).max(255),
          contentType: z.string().trim().min(1).max(100),
          sizeBytes: z.number().int().positive(),
        }),
      )
      .min(1)
      .max(100),
  })
  .strict();
export type BrowserImportPictureUploadsBody = z.infer<typeof BrowserImportPictureUploadsBodySchema>;

export const BrowserImportPictureUploadsResponseSchema = z.object({
  uploads: z.array(CreateMediaUploadResponseSchema),
});
export type BrowserImportPictureUploadsResponse = z.infer<typeof BrowserImportPictureUploadsResponseSchema>;

export const BrowserImportListingBodySchema = z
  .object({
    portalListingId: ScoutIdSchema,
    // ScoutManager's own `realEstate` answer, as read in the agent's session; immoOS maps it (IMM-1484 mapping).
    realEstate: z.record(z.string(), z.unknown()),
    assignedMemberId: z.uuid(),
    pictures: z
      .array(
        z.object({
          fileId: z.uuid(),
          title: z.string().max(255),
          floorplan: z.boolean(),
          titlePicture: z.boolean(),
        }),
      )
      .max(100),
  })
  .strict();
export type BrowserImportListingBody = z.infer<typeof BrowserImportListingBodySchema>;

export const BrowserImportListingResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  objectNumber: z.string(),
  title: z.string(),
});
export type BrowserImportListingResponse = z.infer<typeof BrowserImportListingResponseSchema>;

export const RecordBrowserOutcomeResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  is24: z.array(BrowserIs24ListingSchema),
});
export type RecordBrowserOutcomeResponse = z.infer<typeof RecordBrowserOutcomeResponseSchema>;

// IMM-1407: the operator's read of the calls on the admin MCP. Exactly the job, except each picture's download link,
// which becomes a placeholder: the read reaches a third-party processor (the operator's Claude).
export const OperatorBrowserJobMediaSchema = BrowserJobMediaSchema.omit({ downloadUrl: true }).extend({
  download: z.object({ placeholder: z.literal(true), fileName: z.string(), contentType: z.string() }),
});

export const OperatorBrowserPublishCallsSchema = BrowserJobResponseSchema.extend({
  jobs: z.array(BrowserPublishJobV2Schema.extend({ media: z.array(OperatorBrowserJobMediaSchema) })),
});
export type OperatorBrowserPublishCalls = z.infer<typeof OperatorBrowserPublishCallsSchema>;

// IMM-1406: runs the extension carries out on an Objekt's ImmoScout24 listing.
export const BrowserRunPathParamsSchema = BrowserJobPathParamsSchema.extend({ runId: z.uuid() });

export const StartBrowserRunBodySchema = z.object({ action: z.literal('TRANSMIT') }).strict();
export type StartBrowserRunBody = z.infer<typeof StartBrowserRunBodySchema>;

export const StartBrowserRunResponseSchema = z.object({ run: BrowserRunViewSchema, job: BrowserJobResponseSchema });
export type StartBrowserRunResponse = z.infer<typeof StartBrowserRunResponseSchema>;

export const ReportBrowserRunProgressBodySchema = z
  .object({ done: z.number().int().min(0), total: z.number().int().min(1).max(2000) })
  .strict()
  .refine((body) => body.done <= body.total);
export type ReportBrowserRunProgressBody = z.infer<typeof ReportBrowserRunProgressBodySchema>;

export const FinishBrowserRunBodySchema = z.discriminatedUnion('result', [
  z.object({ result: z.literal('DONE'), notKept: z.array(z.string().trim().min(1).max(120)).max(50) }).strict(),
  z
    .object({
      result: z.literal('FAILED'),
      failure: z.enum(['IS24_REFUSED', 'IS24_SIGNED_OUT', 'EXTENSION_FAILED']),
      portalMessage: z.string().min(1).max(4000).nullable(),
    })
    .strict()
    .refine((body) => (body.failure === 'IS24_REFUSED') === (body.portalMessage !== null)),
]);
export type FinishBrowserRunBody = z.infer<typeof FinishBrowserRunBodySchema>;

export const BrowserRunResponseSchema = z.object({ run: BrowserRunViewSchema });
export type BrowserRunResponse = z.infer<typeof BrowserRunResponseSchema>;

// IMM-1406.4: a confirmed client request runs within this window, measured from the confirmation, or lapses.
export const BROWSER_RUN_CONFIRMED_TTL_MS = 24 * 60 * 60_000;

// Decline keeps the empty body; only a confirmation can carry options (IMM-1406.5).
export const DecideBrowserRunBodySchema = z.object({}).strict();
export type DecideBrowserRunBody = z.infer<typeof DecideBrowserRunBodySchema>;

// IMM-1406.5: a GO_LIVE request's confirmation carries the options the member ticked; SEND / UPDATE carry none.
export const ConfirmBrowserRunBodySchema = z
  .object({ publicationOptions: z.array(PublicationOptionSchema).min(1).max(10).optional() })
  .strict();
export type ConfirmBrowserRunBody = z.infer<typeof ConfirmBrowserRunBodySchema>;

export const BrowserRunQueueResponseSchema = z.object({
  data: z.array(
    z.object({
      runId: z.uuid(),
      organizationId: refersTo('organization', z.uuid()),
      propertyId: refersTo('property', z.uuid()),
      kind: z.enum(BROWSER_RUN_KINDS),
    }),
  ),
});
export type BrowserRunQueueResponse = z.infer<typeof BrowserRunQueueResponseSchema>;

export const ClaimBrowserRunBodySchema = z.object({}).strict();
export type ClaimBrowserRunBody = z.infer<typeof ClaimBrowserRunBodySchema>;

// IMM-1406.3: putting a draft online walks ScoutManager's server-rendered go-live pages, built here like every job.

/** The go-live flow's prefix; a go-live job may load and submit nothing else (IMM-1406, PRD Risks). */
export const SCOUTMANAGER_PAGES_PATH = '/scoutmanager/angebot/';

/** Where ScoutManager's go-live flow lands once the listing is online; the only success check it offers. */
const ScoutManagerLandingSchema = z.string().startsWith('/scoutmanager/');

const ScoutManagerPagePathSchema = z.string().startsWith(SCOUTMANAGER_PAGES_PATH);

export const GoLiveStepSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    path: ScoutManagerPagePathSchema,
    form: z.object({ action: ScoutManagerPagePathSchema.optional() }).strict(),
    set: z.record(z.string(), z.string()),
    // Each named checkbox is one publication option; the chosen ones are posted as checked.
    options: z
      .object({ names: z.array(z.string().min(1)).min(1) })
      .strict()
      .optional(),
    // Loads the URL the previous page's address carries in this query parameter, instead of a form (the skipped booking).
    follow: z
      .object({ param: z.string().min(1) })
      .strict()
      .optional(),
    submitter: z
      .object({ name: z.string().min(1), value: z.string() })
      .strict()
      .optional(),
  })
  .strict();
export type GoLiveStep = z.infer<typeof GoLiveStepSchema>;

// IMM-1409: taking a listing offline and deleting it walk the same kind of pages; only a go-live has an options step.
export const BrowserGoLiveJobSchema = z
  .object({
    v: z.literal(1),
    kind: z.enum(BROWSER_PAGE_RUN_KINDS),
    listingType: z.enum(IMMOSCOUT24_LISTING_TYPES),
    portalListingId: ScoutIdSchema,
    steps: z.array(GoLiveStepSchema).min(1).max(10),
    optionsStep: z.string().optional(),
    verify: z.object({ landsOn: ScoutManagerLandingSchema }).strict(),
  })
  .strict()
  .refine((job) => (job.kind === 'GO_LIVE') === (job.optionsStep !== undefined), {
    message: 'optionsStep is set exactly on a GO_LIVE job',
    path: ['optionsStep'],
  });
export type BrowserGoLiveJob = z.infer<typeof BrowserGoLiveJobSchema>;

export const ClaimBrowserRunResponseSchema = z.object({
  run: BrowserRunViewSchema,
  // null: a GO_LIVE run, or the run ended FAILED / NOT_READY at the claim
  job: BrowserJobResponseSchema.nullable(),
  // IMM-1406.5: a GO_LIVE run's steps, built at the claim; null for SEND / UPDATE and when the run ended NOT_READY
  goLiveJob: BrowserGoLiveJobSchema.nullable(),
});
export type ClaimBrowserRunResponse = z.infer<typeof ClaimBrowserRunResponseSchema>;

export const GoLiveJobQuerySchema = z.object({ listingType: z.enum(IMMOSCOUT24_LISTING_TYPES) });

export const BrowserGoLiveJobResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  job: BrowserGoLiveJobSchema,
});
export type BrowserGoLiveJobResponse = z.infer<typeof BrowserGoLiveJobResponseSchema>;

export const StartGoLiveRunBodySchema = z
  .object({
    listingType: z.enum(IMMOSCOUT24_LISTING_TYPES),
    publicationOptions: z.array(PublicationOptionSchema).min(1).max(10),
  })
  .strict();
export type StartGoLiveRunBody = z.infer<typeof StartGoLiveRunBodySchema>;

export const StartGoLiveRunResponseSchema = z.object({ run: BrowserRunViewSchema, job: BrowserGoLiveJobSchema });
export type StartGoLiveRunResponse = z.infer<typeof StartGoLiveRunResponseSchema>;

// IMM-1409: „Offline nehmen" or „Löschen" confirmed in immoOS; the answer is the run and its steps, like a go-live.
export const StartEndListingRunBodySchema = z
  .object({
    kind: z.enum(BROWSER_END_LISTING_KINDS),
    listingType: z.enum(IMMOSCOUT24_LISTING_TYPES),
  })
  .strict();
export type StartEndListingRunBody = z.infer<typeof StartEndListingRunBodySchema>;
